import { ReactNode } from "react";
import { Control } from "react-hook-form";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSearchSelect } from "../../../../components/ui/Input/FormSearchSelect/FormSearchSelect";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { CompanySelectOption } from "../hooks/useCompanyStaticOptions";
import { CompanyFormData } from "../types/companyForm.types";
import CompanyFormLayout, { CompanySectionHeading } from "./CompanyFormLayout";

export const COMPANY_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" }
];

const MONO = "tw-font-mono";

interface CompanyFormFieldsProps {
  control: Control<CompanyFormData>;
  countries: CompanySelectOption[];
  currencies: CompanySelectOption[];
  isCountriesLoading: boolean;
  isCurrencyLoading: boolean;
  /** The logo uploader differs between register (local file only) and edit (persisted logo + delete), so each flow supplies its own. */
  logoSlot: ReactNode;
}

/**
 * The company form body shared by the Register and Edit drawers (each passes
 * its own RHF `control`): Company identity / Location and subscription /
 * Operating scale sections on the left, and a "Company record" panel
 * (logo + status) on the right. Same fields and validation as the legacy
 * modals — Name / Address / Company Email required, everything else
 * optional — plus an editable Status.
 */
const CompanyFormFields = ({
  control,
  countries,
  currencies,
  isCountriesLoading,
  isCurrencyLoading,
  logoSlot
}: CompanyFormFieldsProps) => (
  <CompanyFormLayout
    main={
      <>
        <section className="tw-flex tw-flex-col tw-gap-5">
          <CompanySectionHeading>Company identity</CompanySectionHeading>
          <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
            <FormInput
              control={control}
              name="name"
              label="Company name"
              required
              placeholder="Company name *"
              errorMessage="Please enter company name"
              rules={{
                validate: (value: string) => !!value?.trim() || "Please enter company name"
              }}
            />
            <FormInput
              control={control}
              name="registrationNumber"
              label="Registration number"
              placeholder="Registration number"
              className={MONO}
            />
            <FormInput
              control={control}
              name="companyEmail"
              label="Company email"
              required
              placeholder="Company email *"
              errorMessage="Please enter company email"
            />
            <FormInput control={control} name="phone" label="Phone" placeholder="Phone" />
            <FormInput control={control} name="website" label="Website" placeholder="Website" />
            <FormInput control={control} name="industry" label="Industry" placeholder="Industry" />
          </div>
        </section>

        <section className="tw-flex tw-flex-col tw-gap-5">
          <CompanySectionHeading>Location and subscription</CompanySectionHeading>
          <FormInput
            control={control}
            name="address"
            label="Address"
            required
            placeholder="Address"
            errorMessage="Please enter address"
          />
          <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
            <FormSearchSelect
              control={control}
              name="tenantCountry"
              label="Country"
              placeholder="Select country"
              options={countries}
              loading={isCountriesLoading}
            />
            <FormInput
              control={control}
              name="zipCode"
              label="Zip code"
              placeholder="Zip code"
              className={MONO}
            />
            <FormSearchSelect
              control={control}
              name="currency"
              label="Currency"
              placeholder="Select currency"
              options={currencies}
              loading={isCurrencyLoading}
            />
            <FormInput
              control={control}
              name="dateOfSubscription"
              label="Date of subscription"
              type="date"
              className={MONO}
            />
          </div>
        </section>

        <section className="tw-flex tw-flex-col tw-gap-5">
          <CompanySectionHeading>Operating scale</CompanySectionHeading>
          <div className="tw-grid tw-grid-cols-2 tw-gap-5 sm:tw-grid-cols-4">
            <FormInputNumber
              control={control}
              name="noOfEmployees"
              label="Employees"
              placeholder="Employees"
              className={MONO}
            />
            <FormInputNumber
              control={control}
              name="noOfUsers"
              label="Users"
              placeholder="Users"
              removeDecimal
              className={MONO}
            />
            <FormInputNumber
              control={control}
              name="noOfProducts"
              label="Products"
              placeholder="Products"
              className={MONO}
            />
            <FormInputNumber
              control={control}
              name="noOfSKUS"
              label="SKUs"
              placeholder="SKUs"
              removeDecimal
              className={MONO}
            />
          </div>
        </section>
      </>
    }
    sidebar={
      <>
        <CompanySectionHeading>Company record</CompanySectionHeading>
        <div className="tw-flex tw-flex-col tw-gap-2">
          <p className="tw-text-sm tw-font-medium tw-text-foreground">Company logo</p>
          {logoSlot}
        </div>
        <FormSelect
          control={control}
          name="status"
          label="Status"
          options={COMPANY_STATUS_OPTIONS}
          placeholder="Select status"
        />
      </>
    }
  />
);

export default CompanyFormFields;
